import { useContext } from "react";
import { Avatar, HStack, VStack, Text } from "@chakra-ui/react";
import { WfLinkGhost, WfLinkUnstyled } from "src/components/base/wf-link";
import { CreationDate } from "src/components/common/creation-date/creation-date";
import { FeedCardContext } from "src/components/feed-card/feed-card";
import { IFeedCardActor } from "src/components/feed-card/types/feed-card";
import { IconAvatar } from "src/components/icons";
import { EnumFeedClickTrackingArea, useFeedClickTracking } from "src/components/pages/feed/hooks/use-feed-click-tracking";
import { triggerCallbackOnMiddleClickEvent } from "src/utils/dom/mouse-util";
import { isUrlPathnameSameAsCurrentPage } from "./utils/link-helper";

const FEED_CARD_ACTOR_TEST_ID = "feed-card-actor";

export interface IFeedCardActorProps extends IFeedCardActor {
    date: string;
}

interface IFeedCardActorContentProps extends IFeedCardActorProps {
    onActorClick?: () => void;
    onWikifolioClick?: () => void;
}

export const FeedCardActorContent = ({ name, url, date, image, wikifolio, onActorClick, onWikifolioClick }: IFeedCardActorContentProps) => {
    return (
        <HStack spacing={1} align="flex-start" data-test-id={FEED_CARD_ACTOR_TEST_ID}>
            <WfLinkUnstyled target="_blank" href={url} onClick={onActorClick} onAuxClick={triggerCallbackOnMiddleClickEvent(onActorClick)}>
                <Avatar
                    bg="transparent"
                    src={image.url}
                    name={image.alt ?? undefined}
                    icon={<IconAvatar color="gray.800" boxSize="100%" />}
                    loading="lazy"
                />
            </WfLinkUnstyled>
            <VStack flexDir="column" align="flex-start" flexGrow={1} overflow="hidden" spacing={0}>
                <WfLinkGhost
                    target="_blank"
                    fontWeight="bold"
                    href={url}
                    onClick={onActorClick}
                    onAuxClick={triggerCallbackOnMiddleClickEvent(onActorClick)}
                >
                    {name}
                </WfLinkGhost>
                {wikifolio &&
                    (isUrlPathnameSameAsCurrentPage(wikifolio.url) ? (
                        <Text fontSize="sm" noOfLines={1} wordBreak="break-all">
                            {wikifolio.shortDescription}
                        </Text>
                    ) : (
                        <WfLinkGhost
                            target="_blank"
                            href={wikifolio.url}
                            fontWeight="normal"
                            fontSize="sm"
                            noOfLines={1}
                            wordBreak="break-all"
                            onClick={onWikifolioClick}
                            onAuxClick={triggerCallbackOnMiddleClickEvent(onWikifolioClick)}
                        >
                            {wikifolio.shortDescription}
                        </WfLinkGhost>
                    ))}
                <CreationDate date={date} />
            </VStack>
        </HStack>
    );
};

export const FeedCardActor = ({ name, url, date, image, wikifolio }: IFeedCardActorProps) => {
    const context = useContext(FeedCardContext);
    const { trackFeedClick } = useFeedClickTracking();

    const onActorClick = () => {
        trackFeedClick(context.contentId, url, EnumFeedClickTrackingArea.Actor);
    };

    const onWikifolioClick = () => {
        if (wikifolio?.url) {
            trackFeedClick(context.contentId, wikifolio.url, EnumFeedClickTrackingArea.Actor);
        }
    };

    return (
        <FeedCardActorContent
            name={name}
            url={url}
            date={date}
            image={image}
            wikifolio={wikifolio}
            onActorClick={onActorClick}
            onWikifolioClick={onWikifolioClick}
        />
    );
};
